import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import { VisuallyHidden } from '@n26/nxd'
import {
  TIMEFRAME_FILTER,
  type CardStub,
  type ChartCard,
  type DashboardSection,
  type Timeframe,
} from '@/features/dashboard/api/schemas'
import { useDashboardStructure } from '@/features/dashboard/api/queries'
import { TimeframeFilters } from '@/features/dashboard/filters/SectionFilters'
import { useSectionSelection } from '@/features/dashboard/filters/useDashboardFilters'
import {
  previousPeriodLabel,
  toFigure,
  toFigureAt,
} from '@/features/dashboard/format/figure'
import { useFilteredCard } from '@/features/dashboard/filters/useFilteredCard'
import {
  formatMonthLong,
  formatMonthYearLong,
  formatQuarterLabel,
} from '@/features/dashboard/format/format'
import { deepDiveLabel, deepDiveOf } from '@/features/dashboard/model/sections'
import { deepDivePath } from '@/shared/routes'
import { cx } from '@/shared/lib/classNames'
import { homeCardAnchor } from '@/features/dashboard/model/homeReturn'
import { ArrowLeftIcon, ArrowRightIcon } from '@/shared/ui/icons'
import {
  cardTransitionName,
  viewTransitionStyle,
} from '@/shared/lib/viewTransition'
import { CardFigure } from './CardFigure'
import { EmptyCardMessage } from './EmptyCardMessage'
import { DataTable } from '../charts/DataTable'
import { ExpandedBarChart } from '../charts/ExpandedBarChart'
import { chartToTable } from '../charts/lib/chartTable'
import { useViewAsTable } from '@/features/dashboard/filters/useViewAsTable'
import { expandedBarModel } from '../charts/lib/expandedBarOptions'
import {
  latestDataIndex,
  maskBudgets,
  periodWindow,
} from '../charts/lib/series'
import { annualOutlookOf, statusSections } from '../charts/lib/statuses'
import { DeltaBadge } from './DeltaBadge'
import { InfoTip } from '@/shared/ui/InfoTip'
import { MarketFilterNote } from './MarketFilterNote'
import styles from './ExpandedModule.module.css'

type ExpandedModuleProps = {
  stub: CardStub
  /** The section the card belongs to, whose timeframe and range it follows. */
  section: DashboardSection
  /** Whether to move focus into the panel (it was opened from the keyboard). */
  takeFocus?: boolean
  /** `fromKeyboard`: the back arrow was pressed with a key rather than clicked. */
  onClose?: (fromKeyboard: boolean) => void
  /**
   * Heads a deep dive rather than opening from a tile: no back arrow, no
   * deep-dive link, and no view transition. The card is otherwise the same.
   */
  embedded?: boolean
}

const TRUNCATED_AXIS_NOTE =
  '* Note: Y-axis truncated to highlight the variation.'

/**
 * A tile expanded into the panel beside the sidebar: the chart on the left,
 * with the back arrow and the section's timeframe above it, and on the right
 * the card's title, period, figure and statuses.
 */
export const ExpandedModule = ({
  stub,
  section,
  takeFocus = false,
  onClose,
  embedded = false,
}: ExpandedModuleProps) => {
  const {
    data: card,
    isPending,
    isError,
    isRefreshing,
    clearMarkets,
    comparison,
    timeframe,
    months,
  } = useFilteredCard(stub, section.id)
  const backRef = useRef<HTMLButtonElement>(null)

  // Only a keyboard opening takes focus: focus inside would show hover-only controls.
  useEffect(() => {
    if (takeFocus) backRef.current?.focus({ preventScroll: true })
  }, [takeFocus])

  const { data: dashboard } = useDashboardStructure()
  const { selection } = useSectionSelection(section.id)
  const rawChart = card?.kind === 'chart' ? card : null
  // Budgets are masked to their months and the chart cut to the latest reported period.
  const chart = useMemo(
    () => rawChart && periodWindow(maskBudgets(rawChart), timeframe, months),
    [rawChart, timeframe, months]
  )
  const takesTimeframe = stub.filters?.includes(TIMEFRAME_FILTER) ?? false
  // 'View as table' lists the headline's figures, as the page's other charts.
  const { asTable } = useViewAsTable()
  const table = useMemo(
    () => (embedded && asTable && chart ? chartToTable(chart, null) : null),
    [embedded, asTable, chart]
  )

  // The bar the panel speaks of: the one clicked, else the latest reported.
  const [pickedCategory, setPickedCategory] = useState<string | null>(null)
  const latest = chart ? latestDataIndex(chart) : -1
  const picked = chart ? chart.categories.indexOf(pickedCategory ?? '') : -1
  const selectedIndex = picked >= 0 && picked <= latest ? picked : latest
  const onSelect = useCallback(
    (index: number) => setPickedCategory(chart?.categories[index] ?? null),
    [chart]
  )

  const reportsEarlierBar =
    chart && selectedIndex >= 0 && selectedIndex !== latest
  const figure = reportsEarlierBar
    ? toFigureAt(chart, selectedIndex, previousPeriodLabel(timeframe))
    : card
      ? toFigure(card, { expanded: true, comparison })
      : null
  // The chart's axis starting above 0 is flagged in the info tooltip.
  const truncated = useMemo(
    () =>
      chart !== null &&
      chart.categories.length > 0 &&
      expandedBarModel(chart, timeframe, selectedIndex).truncated,
    [chart, timeframe, selectedIndex]
  )
  const statuses = chart ? statusSections(chart, timeframe, selectedIndex) : []
  const lowerIsBetter = figure?.lowerIsBetter ?? false
  const rangeLabel = dashboard?.filters?.timeframes
    ?.find(option => option.id === selection?.timeframe)
    ?.periods.options.find(option => option.value === selection?.periods)?.label
  const dive = useMemo(
    () =>
      dashboard && !embedded ? deepDiveOf(dashboard.sections, stub) : undefined,
    [dashboard, embedded, stub]
  )

  return (
    <article
      id={embedded ? undefined : homeCardAnchor(stub.cardId)}
      className={cx(
        styles.card,
        embedded && styles.embedded,
        isRefreshing && styles.busy
      )}
      aria-busy={isRefreshing || undefined}
      aria-labelledby={`expanded-${stub.cardId}`}
      style={
        embedded
          ? undefined
          : viewTransitionStyle(cardTransitionName(stub.cardId))
      }
    >
      <div className={styles.main}>
        <div className={styles.mainHeader}>
          {!embedded && (
            <button
              ref={backRef}
              type='button'
              className={styles.back}
              aria-label='Close expanded view'
              // A key press clicks with a detail of 0.
              onClick={event => onClose?.(event.detail === 0)}
            >
              <ArrowLeftIcon />
            </button>
          )}
          {takesTimeframe && !embedded && (
            <TimeframeFilters section={section} />
          )}
        </div>
        {card?.kind === 'empty' && (
          <EmptyCardMessage
            reason={card.reason}
            onClear={clearMarkets}
            className={styles.empty}
          />
        )}
        {isPending && (
          <div className={styles.skeleton} role='status'>
            <VisuallyHidden>Loading</VisuallyHidden>
          </div>
        )}
        {isError && (
          <p className={styles.error} role='alert'>
            Couldn&apos;t load this card.
          </p>
        )}
        {table && (
          <div className={styles.tableSlot}>
            <DataTable table={table} title={stub.name} unit={chart?.unit} />
          </div>
        )}
        {chart && chart.categories.length > 0 && !table && (
          <ExpandedBarChart
            card={chart}
            timeframe={timeframe}
            selectedIndex={selectedIndex}
            onSelect={onSelect}
            className={embedded ? styles.chartFloor : undefined}
          />
        )}
      </div>
      <aside className={styles.side}>
        <div className={styles.header}>
          <div className={styles.titles}>
            <h3 id={`expanded-${stub.cardId}`} className={styles.title}>
              {stub.name}
            </h3>
            {chart && (
              <p className={styles.subtitle}>
                {subtitle(
                  chart,
                  takesTimeframe ? timeframe : undefined,
                  selectedIndex,
                  rangeLabel
                )}
              </p>
            )}
          </div>
          {stub.description && (
            <InfoTip
              name={stub.name}
              description={
                truncated
                  ? `${stub.description}\n\n${TRUNCATED_AXIS_NOTE}`
                  : stub.description
              }
              className={styles.info}
            />
          )}
        </div>
        {figure && (
          <CardFigure
            figure={figure}
            size='expanded'
            className={styles.figure}
          />
        )}
        {chart?.marketFiltered === false && <MarketFilterNote />}
        {statuses.map(({ title, total, totalLabel, rows }) => (
          <section key={title} className={styles.status}>
            <h4 className={styles.statusTitle}>{title}</h4>
            <ul className={styles.rows}>
              {total && (
                <li className={styles.row}>
                  <span>{totalLabel}</span>
                  <span className={styles.total}>{total}</span>
                </li>
              )}
              {rows.map(row => (
                <li key={row.label} className={styles.row}>
                  <span>{row.label}</span>
                  <DeltaBadge
                    change={row.pct / 100}
                    direction={row.pct >= 0 ? 'up' : 'down'}
                    lowerIsBetter={lowerIsBetter}
                    size='row'
                  >
                    {row.diff && (
                      <span aria-hidden='true'>&nbsp;▪ {row.diff}</span>
                    )}
                  </DeltaBadge>
                  {row.diff && <VisuallyHidden>{row.diff}</VisuallyHidden>}
                </li>
              ))}
            </ul>
          </section>
        ))}
        {dive && (
          <div className={styles.footer}>
            <Link
              to={deepDivePath(dive.id)}
              className={styles.dive}
              // Starts with the visible text, and says which deep dive opens.
              aria-label={`Deep dive: ${deepDiveLabel(dive.title)}`}
            >
              Deep dive
              <ArrowRightIcon />
            </Link>
          </div>
        )}
      </aside>
    </article>
  )
}

/**
 * The period the figure is of, named like 'September 2026 • YTD • Current
 * Year', 'September 2026', '2026-Q3', '2026 Outlook'. A card not taking the
 * timeframe has its bar's month and its own period.
 */
const subtitle = (
  card: ChartCard,
  timeframe: Timeframe | undefined,
  index: number,
  rangeLabel: string | undefined
) => {
  const category = card.categories[index]
  switch (timeframe) {
    case undefined:
      return [
        category === undefined ? null : formatMonthLong(category),
        card.period?.label,
      ]
        .filter(Boolean)
        .join(' • ')
    case 'YTD':
      return [
        category === undefined ? null : formatMonthLong(category),
        'YTD',
        rangeLabel,
      ]
        .filter(Boolean)
        .join(' • ')
    case 'Quarterly':
      return formatQuarterLabel(category ?? '')
    case 'Annual':
      return annualOutlookOf(card, timeframe) === undefined
        ? `${new Date().getFullYear()} YTD`
        : `${category} Outlook`
    default:
      return category === undefined
        ? 'Timeframe: Monthly'
        : formatMonthYearLong(category)
  }
}
